/**
 * The scale measurements are taken at: a ratio ("1 in = 20 ft"), or
 * calibrated from a line drawn along something of known length.
 */

import { useState } from 'react'
import type { PageScale } from '../api'

const UNITS = ['in', 'ft', 'yd', 'mi', 'mm', 'cm', 'm', 'km']

interface Props {
  pageIndex: number
  pageCount: number
  current: PageScale[]
  /** Set when the user has just drawn a calibration line. */
  calibration: number[] | null
  onRatio: (paper: number, paperUnit: string, real: number, realUnit: string, allPages: boolean) => void
  onCalibrate: (points: number[], real: number, realUnit: string, allPages: boolean) => void
  onStartCalibrating: () => void
  onRemove: (allPages: boolean) => void
  onClose: () => void
}

export function ScaleDialog(props: Props) {
  const own = props.current.find((s) => s.source === 'odie')
  const drawing = props.current.filter((s) => s.source === 'document')
  const [paper, setPaper] = useState('1')
  const [paperUnit, setPaperUnit] = useState('in')
  const [real, setReal] = useState(props.calibration ? '' : '20')
  const [realUnit, setRealUnit] = useState('ft')
  const [all, setAll] = useState(props.pageCount > 1)
  const calibrating = Boolean(props.calibration)
  const length = props.calibration
    ? Math.hypot(props.calibration[2] - props.calibration[0], props.calibration[3] - props.calibration[1]) / 72
    : 0

  const apply = () => {
    const r = Number(real)
    if (!(r > 0)) return
    if (props.calibration) props.onCalibrate(props.calibration, r, realUnit, all)
    else if (Number(paper) > 0) props.onRatio(Number(paper), paperUnit, r, realUnit, all)
  }

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-labelledby="scale-title">
      <form className="modal__panel" onSubmit={(e) => { e.preventDefault(); apply() }}>
        <h2 className="modal__title" id="scale-title">{calibrating ? 'Calibrate the scale' : 'Scale'}</h2>
        <div className="modal__body">
          {drawing.length > 0 && !calibrating && (
            <p className="modal__hint">
              This drawing carries its own scale{drawing.length > 1 ? 's' : ''}:{' '}
              {drawing.map((s) => `${s.ratio}${s.name ? ` (${s.name})` : ''}`).join('; ')}.
              Measurements inside {drawing.length > 1 ? 'those views' : 'it'} use {drawing.length > 1 ? 'them' : 'it'}; the scale below covers the rest.
            </p>
          )}
          {own && !calibrating && <p>Current scale: <strong>{own.ratio}</strong></p>}
          {calibrating ? (
            <>
              <p>The line you drew is {length.toFixed(2)} in on the page. How long is it really?</p>
              <div className="field-row">
                <label className="field">
                  <span>Real length</span>
                  <input type="number" min={0} step="any" value={real} autoFocus onChange={(e) => setReal(e.target.value)} />
                </label>
                <label className="field">
                  <span>Unit</span>
                  <select value={realUnit} onChange={(e) => setRealUnit(e.target.value)}>
                    {UNITS.map((u) => <option key={u}>{u}</option>)}
                  </select>
                </label>
              </div>
            </>
          ) : (
            <div className="scale__ratio">
              <input type="number" min={0} step="any" value={paper} aria-label="On the page"
                     onChange={(e) => setPaper(e.target.value)} />
              <select value={paperUnit} onChange={(e) => setPaperUnit(e.target.value)} aria-label="Page unit">
                {['in', 'mm', 'cm'].map((u) => <option key={u}>{u}</option>)}
              </select>
              <span>=</span>
              <input type="number" min={0} step="any" value={real} aria-label="In reality"
                     onChange={(e) => setReal(e.target.value)} />
              <select value={realUnit} onChange={(e) => setRealUnit(e.target.value)} aria-label="Real unit">
                {UNITS.map((u) => <option key={u}>{u}</option>)}
              </select>
            </div>
          )}
          {props.pageCount > 1 && (
            <label className="check">
              <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} />
              Every page (otherwise page {props.pageIndex + 1} only)
            </label>
          )}
          {!calibrating && (
            <p className="modal__hint">
              Don&rsquo;t know the scale? <button type="button" className="linkbtn" onClick={props.onStartCalibrating}>
              Calibrate from a known length</button>: draw along a dimension you know, then type its real length.
            </p>
          )}
        </div>
        <div className="modal__footer">
          {own && !calibrating && (
            <button type="button" className="btn btn--danger" style={{ marginRight: 'auto' }}
                    onClick={() => props.onRemove(all)}>Remove scale</button>
          )}
          <button type="button" className="btn" onClick={props.onClose}>Cancel</button>
          <button type="submit" className="btn btn--primary">Set scale</button>
        </div>
      </form>
    </div>
  )
}
